// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { Users, Building2, Briefcase, KeyRound } from 'lucide-react';
import { useAuth } from '@/subsystems/workspaces';
import { UserManagement } from '@/subsystems/workspaces/components/users';
import { WorkspaceMembersPanel } from '@/subsystems/workspaces/components/workspace-admin/WorkspaceMembersPanel';
import { WorkspaceVaultAccessPanel } from '@/subsystems/vault/components/WorkspaceVaultAccessPanel';

const USER_TABS = {
  all: 'all',
  org: 'org',
  workspace: 'workspace',
  vault: 'vault',
} as const;

export default function UsersPage() {
  const {
    isGlobalAdmin,
    isOrgAdmin,
    isWorkspaceAdmin,
    selectedOrganizationId,
    selectedWorkspaceId,
  } = useAuth();

  // Org administrators receive the effective workspace-admin role from the
  // workspace list without exposing global user records.
  const canManageWorkspaceTabs = isGlobalAdmin || isWorkspaceAdmin;
  const canManageOrganization = isGlobalAdmin || isOrgAdmin;

  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const defaultTab = isGlobalAdmin
    ? USER_TABS.all
    : isOrgAdmin
      ? USER_TABS.org
      : USER_TABS.workspace;
  const allowedTabs = [
    ...(isGlobalAdmin ? [USER_TABS.all] : []),
    ...(canManageOrganization ? [USER_TABS.org] : []),
    ...(canManageWorkspaceTabs ? [USER_TABS.workspace, USER_TABS.vault] : []),
  ];
  const [internalTab, setInternalTab] = useState(urlTab ?? defaultTab);
  const requestedTab = urlTab ?? internalTab;
  const currentTab = allowedTabs.includes(requestedTab as typeof allowedTabs[number])
    ? requestedTab
    : defaultTab;

  const handleTabChange = useCallback((tabId: string) => {
    setInternalTab(tabId);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabId);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const debugMode = import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true';

  return (
    <div data-testid="users-page" className="docreview-users-page mx-auto max-w-[1240px] px-6 pb-12 pt-6">
      <h1 className="text-[30px] font-bold leading-[1.15] tracking-[-0.02em] text-[#0f172a]" data-testid="users-page-heading">User Management</h1>

      <Tabs value={currentTab} onValueChange={handleTabChange} className="mt-[22px] gap-0">
        <TabsList className="docreview-user-tabs mx-auto flex-wrap">
          {isGlobalAdmin && (
            <TabsTrigger className="docreview-user-tab" value={USER_TABS.all} data-testid="users-all-tab">
              <Users className="h-4 w-4" />
              All Users
            </TabsTrigger>
          )}
          {canManageOrganization && (
            <TabsTrigger className="docreview-user-tab" value={USER_TABS.org} data-testid="users-org-tab">
              <Building2 className="h-4 w-4" />
              Org Users
            </TabsTrigger>
          )}
          {canManageWorkspaceTabs && (
            <TabsTrigger className="docreview-user-tab" value={USER_TABS.workspace} data-testid="users-workspace-tab">
              <Briefcase className="h-4 w-4" />
              Workspace Users
            </TabsTrigger>
          )}
          {canManageWorkspaceTabs && (
            <TabsTrigger className="docreview-user-tab" value={USER_TABS.vault} data-testid="users-vault-tab">
              <KeyRound className="h-4 w-4" />
              Vault Access
            </TabsTrigger>
          )}
        </TabsList>

        {isGlobalAdmin && (
          <TabsContent value={USER_TABS.all} className="mt-[26px]">
            <UserManagement app_id="docreview" debug={debugMode} />
          </TabsContent>
        )}

        {canManageOrganization && (
          <TabsContent value={USER_TABS.org} className="mt-[26px]">
            {selectedOrganizationId ? (
              <UserManagement
                app_id="docreview"
                org_id={selectedOrganizationId}
                debug={debugMode}
              />
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No organization selected.
              </div>
            )}
          </TabsContent>
        )}

        <TabsContent value={USER_TABS.workspace} className="mt-[26px]">
          {selectedOrganizationId && selectedWorkspaceId ? (
            <WorkspaceMembersPanel
              orgId={selectedOrganizationId}
              wsId={selectedWorkspaceId}
            />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No workspace selected.
            </div>
          )}
        </TabsContent>

        <TabsContent value={USER_TABS.vault} className="mt-[26px]">
          {selectedOrganizationId && selectedWorkspaceId ? (
            <WorkspaceVaultAccessPanel
              orgId={selectedOrganizationId}
              workspaceId={selectedWorkspaceId}
            />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No workspace selected.
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
